import React, { useEffect, useCallback, useRef, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import MovieCard from './MovieCard';
import SeriesCard from './SeriesCard';
import MoviesModal from './MovieModal';
import SeriesModal from './SeriesModal';
import { fetchMovieDetails, fetchSeriesDetails } from '../services/tmdb';

// ============================================================
//  GIFT‑CODE SYSTEM
// ============================================================
export const ACTIVE_GIFT_CODES = ['TOMANDJERRY', 'LUCK'];
export const REVOKED_CODES = [''];
export const isUserAdFree = () => {
  const redeemedCode = localStorage.getItem('adFreeCode');
  if (!redeemedCode) return false;
  return !REVOKED_CODES.includes(redeemedCode);
};

// ============================================================
//  AD TIMING CONSTANTS
// ============================================================
const AD_CLICKS_REQUIRED = 3;
const AD_SHOW_DELAY_SECONDS = 8;
const AD_COOLDOWN_HOURS = 1;
const AD_VISIT_INTERVAL_HOURS = 0.9;
const AD_FREE_MESSAGE_DURATION_SECONDS = 5;
const AD_RETURN_UPDATE_DELAY_SECONDS = 1;
const AD_BAR_APPEAR_DELAY_SECONDS = 2;
const AD_COMPLETED_MESSAGE_DELAY_SECONDS = 2;
const AD_WATCH_PATH_GRACE_SECONDS = 3;
const AD_WATCH_PATHS = ['/watch/', '/alt-watch/', '/alt-watch-two/', '/movie/', '/series/'];

// ============================================================
//  FULL TRANSLATIONS (UI strings)
// ============================================================
const translations = {
  en: {
    recommended: 'Home',
    trending: 'Trending',
    movies: 'Movies',
    series: 'Series',
    actors: 'Actors',
    myList: 'My List',
    searchPlaceholderTitle: 'e.g. Rush Hour 3; Furiosa;',
    searchPlaceholderKeyword: 'e.g. Shark; Zombie; Revenge;',
    recommendBtn: 'Suggest something',
    somethingElseBtn: 'Something else',
    gotItBtn: 'Got it',
    themeDiscoveryTitle: 'You discovered Theme searching mode!',
    themeDiscoverySubtitle: 'You can now easily find the exact movie you\'re in the mood for. Some suggestions: (Clickable)',
    historyTitle: 'Recently Viewed',
    historyEmpty: 'Your viewing history is empty',
    loadingIndicator: 'Browsing...',
    showMore: 'Show More',
    cooldownLabel: 'Cooldown ({seconds}s)',
    noMoreResults: 'All content has been displayed',
    noResultsTitle: 'We couldn\'t find such title, check for spelling mistakes',
    noResultsKeyword: 'Check your spelling, try both singular and plural forms, or use alternative words that match the theme',
    removeFromHistoryAria: 'Remove from history',
    clearSearchAria: 'Clear search',
    searchByTitle: 'Search by title',
    searchByKeyword: 'Search by keyword',
    titleMode: 'Title',
    themeMode: 'Theme',
  },
  fr: {
    recommended: 'Recommandations',
    trending: 'Tendances',
    movies: 'Films',
    series: 'Séries',
    actors: 'Acteurs',
    myList: 'Ma liste',
    searchPlaceholderTitle: 'ex. Rush Hour 3; Furiosa;',
    searchPlaceholderKeyword: 'ex. Shark; Zombie; Revenge;',
    recommendBtn: 'Suggérer quelque chose',
    somethingElseBtn: 'Autre chose',
    gotItBtn: 'Compris',
    themeDiscoveryTitle: 'Vous avez découvert le mode de recherche par Thème !',
    themeDiscoverySubtitle: 'Vous pouvez maintenant trouver facilement le film exact que vous recherchez. Quelques suggestions :',
    historyTitle: 'Récemment consultés',
    historyEmpty: 'Votre historique de visionnage est vide',
    loadingIndicator: 'Recherche en cours...',
    showMore: 'Afficher plus',
    cooldownLabel: 'Temps d\'attente ({seconds}s)',
    noMoreResults: 'Tout le contenu a été affiché',
    noResultsTitle: 'Nous n\'avons pas trouvé ce titre, vérifiez l\'orthographe',
    noResultsKeyword: 'Vérifiez l\'orthographe, essayez les formes singulières et plurieles, ou utilisez des mots alternatifs correspondant au thème',
    removeFromHistoryAria: 'Supprimer de l\'historique',
    clearSearchAria: 'Effacer la recherche',
    searchByTitle: 'Rechercher par titre',
    searchByKeyword: 'Rechercher par thème',
    titleMode: 'Titre',
    themeMode: 'Thème',
  },
  es: {
    recommended: 'Recomendadas',
    trending: 'Tendencias',
    movies: 'Películas',
    series: 'Series',
    actors: 'Actores',
    myList: 'Mi lista',
    searchPlaceholderTitle: 'p. ej. Rush Hour 3; Furiosa;',
    searchPlaceholderKeyword: 'p. ej. Shark; Zombie; Revenge;',
    recommendBtn: 'Sugerir algo',
    somethingElseBtn: 'Otra cosa',
    gotItBtn: 'Entendido',
    themeDiscoveryTitle: '¡Has descubierto el modo de búsqueda por Tema!',
    themeDiscoverySubtitle: 'Ahora puedes encontrar fácilmente la película exacta que buscas. Algunas sugerencias:',
    historyTitle: 'Vistos recientemente',
    historyEmpty: 'Tu historial de visualización está vacío',
    loadingIndicator: 'Buscando...',
    showMore: 'Mostrar más',
    cooldownLabel: 'Enfriamiento ({seconds}s)',
    noMoreResults: 'Todo el contenido ha sido mostrado',
    noResultsTitle: 'No pudimos encontrar ese título, revisa la ortografía',
    noResultsKeyword: 'Revisa la ortografía, prueba con formas singulares y plurales, o usa palabras alternativas que coincidan con el tema',
    removeFromHistoryAria: 'Eliminar del historial',
    clearSearchAria: 'Limpiar búsqueda',
    searchByTitle: 'Buscar por título',
    searchByKeyword: 'Buscar por tema',
    titleMode: 'Título',
    themeMode: 'Tema',
  },
  it: {
    recommended: 'Consigliati',
    trending: 'Tendenze',
    movies: 'Film',
    series: 'Serie TV',
    actors: 'Attori',
    myList: 'La mia lista',
    searchPlaceholderTitle: 'es. Rush Hour 3; Furiosa;',
    searchPlaceholderKeyword: 'es. Shark; Zombie; Revenge;',
    recommendBtn: 'Suggerisci qualcosa',
    somethingElseBtn: 'Qualcos\'altro',
    gotItBtn: 'Capito',
    themeDiscoveryTitle: 'Hai scoperto la modalità di ricerca per Tema!',
    themeDiscoverySubtitle: 'Ora puoi trovare facilmente il film esatto che stai cercando. Alcuni suggerimenti:',
    historyTitle: 'Visti di recente',
    historyEmpty: 'La tua cronologia di visualizzazione è vuota',
    loadingIndicator: 'Navigazione in corso...',
    showMore: 'Mostra di più',
    cooldownLabel: 'Attesa ({seconds}s)',
    noMoreResults: 'Tutto il contenuto è stato visualizzato',
    noResultsTitle: 'Non abbiamo trovato questo titolo, controlla l\'ortografia',
    noResultsKeyword: 'Controlla l\'ortografia, prova forme singolari e plurali, o usa parole alternative che corrispondano al tema',
    removeFromHistoryAria: 'Rimuovi dalla cronologia',
    clearSearchAria: 'Cancella ricerca',
    searchByTitle: 'Cerca per titolo',
    searchByKeyword: 'Cerca per tema',
    titleMode: 'Titolo',
    themeMode: 'Tema',
  },
  nl: {
    recommended: 'Aanbevolen',
    trending: 'Trending',
    movies: 'Films',
    series: 'Series',
    actors: 'Acteurs',
    myList: 'Mijn lijst',
    searchPlaceholderTitle: 'bv. Rush Hour 3; Furiosa;',
    searchPlaceholderKeyword: 'bv. Shark; Zombie; Revenge;',
    recommendBtn: 'Iets suggereren',
    somethingElseBtn: 'Iets anders',
    gotItBtn: 'Begrepen',
    themeDiscoveryTitle: 'Je hebt de themazoekmodus ontdekt!',
    themeDiscoverySubtitle: 'Je kunt nu gemakkelijk de exacte film vinden waar je zin in hebt. Enkele suggesties:',
    historyTitle: 'Recent bekeken',
    historyEmpty: 'Je kijkgeschiedenis is leeg',
    loadingIndicator: 'Bezig met zoeken...',
    showMore: 'Meer tonen',
    cooldownLabel: 'Afkoeling ({seconds}s)',
    noMoreResults: 'Alle inhoud is weergegeven',
    noResultsTitle: 'We konden die titel niet vinden, controleer de spelling',
    noResultsKeyword: 'Controleer de spelling, probeer zowel enkelvoud als meervoud, of gebruik alternatieve woorden die bij het thema passen',
    removeFromHistoryAria: 'Verwijder uit geschiedenis',
    clearSearchAria: 'Zoekopdracht wissen',
    searchByTitle: 'Zoeken op titel',
    searchByKeyword: 'Zoeken op thema',
    titleMode: 'Titel',
    themeMode: 'Thema',
  },
};

// ============================================================
//  THEME TRANSLATIONS (all 100 themes, 5 languages)
// ============================================================
const themeTranslations = {
  en: {
    'Shark': 'Shark',
    'Ghost': 'Ghost',
    'Game': 'Game',
    'Killer': 'Killer',
    'Revenge': 'Revenge',
    'Space': 'Space',
    'Love': 'Love',
    'Heist': 'Heist',
    'Camping': 'Camping',
    'Friends': 'Friends',
    'Zombie': 'Zombie',
    'Vampire': 'Vampire',
    'Witch': 'Witch',
    'Superhero': 'Superhero',
    'Villain': 'Villain',
    'Spy': 'Spy',
    'Detective': 'Detective',
    'Prison': 'Prison',
    'Escape': 'Escape',
    'Survival': 'Survival',
    'Apocalypse': 'Apocalypse',
    'Dystopia': 'Dystopia',
    'Time travel': 'Time travel',
    'Parallel universe': 'Parallel universe',
    'Alien': 'Alien',
    'Robot': 'Robot',
    'Nightmare': 'Nightmare',
    'Mind control': 'Mind control',
    'Time Loop': 'Time Loop',
    'Magic': 'Magic',
    'Curse': 'Curse',
    'Legend': 'Legend',
    'Samurai': 'Samurai',
    'Ninja': 'Ninja',
    'Pirate': 'Pirate',
    'War': 'War',
    'Revolution': 'Revolution',
    'Rebellion': 'Rebellion',
    'Conspiracy': 'Conspiracy',
    'Mafia': 'Mafia',
    'Gangster': 'Gangster',
    'Drugs': 'Drugs',
    'Addiction': 'Addiction',
    'Lawyer': 'Lawyer',
    'Vigilante': 'Vigilante',
    'Disaster': 'Disaster',
    'Volcano': 'Volcano',
    'Tsunami': 'Tsunami',
    'Exploration': 'Exploration',
    'Jungle': 'Jungle',
    'Desert': 'Desert',
    'Arctic': 'Arctic',
    'Ocean': 'Ocean',
    'Mystery': 'Mystery',
    'Secret': 'Secret',
    'Music': 'Music',
    'Dance': 'Dance',
    'Boxing': 'Boxing',
    'Racing': 'Racing',
    'Martial arts': 'Martial arts',
    'Gym': 'Gym',
    'School': 'School',
    'College': 'College',
    'Student': 'Student',
    'Bully': 'Bully',
    'Family': 'Family',
    'Marriage': 'Marriage',
    'Friendship': 'Friendship',
    'Betrayal': 'Betrayal',
    'Redemption': 'Redemption',
    'Sacrifice': 'Sacrifice',
    'Riot': 'Riot',
    'Stalker': 'Stalker',
    'Manhunt': 'Manhunt',
    'Ambush': 'Ambush',
    'Hostage': 'Hostage',
    'Robbery': 'Robbery',
    'Smuggling': 'Smuggling',
    'Chase': 'Chase',
    'Hunt': 'Hunt',
    'Hitman': 'Hitman',
    'Assassin': 'Assassin',
    'Blackmail': 'Blackmail',
    'Gang': 'Gang',
    'Sabotage': 'Sabotage',
  },
  fr: {
    'Shark': 'Requin',
    'Ghost': 'Fantôme',
    'Game': 'Jeu',
    'Killer': 'Tueur',
    'Revenge': 'Vengeance',
    'Space': 'Espace',
    'Love': 'Amour',
    'Heist': 'Casse',
    'Camping': 'Camping',
    'Friends': 'Amis',
    'Zombie': 'Zombie',
    'Vampire': 'Vampire',
    'Witch': 'Sorcière',
    'Superhero': 'Super-héros',
    'Villain': 'Méchant',
    'Spy': 'Espion',
    'Detective': 'Détective',
    'Prison': 'Prison',
    'Escape': 'Évasion',
    'Survival': 'Survie',
    'Apocalypse': 'Apocalypse',
    'Dystopia': 'Dystopie',
    'Time travel': 'Voyage dans le temps',
    'Parallel universe': 'Univers parallèle',
    'Alien': 'Extraterrestre',
    'Robot': 'Robot',
    'Nightmare': 'Cauchemar',
    'Mind control': 'Contrôle mental',
    'Time Loop': 'Boucle temporelle',
    'Magic': 'Magie',
    'Curse': 'Malédiction',
    'Legend': 'Légende',
    'Samurai': 'Samouraï',
    'Ninja': 'Ninja',
    'Pirate': 'Pirate',
    'War': 'Guerre',
    'Revolution': 'Révolution',
    'Rebellion': 'Rébellion',
    'Conspiracy': 'Complot',
    'Mafia': 'Mafia',
    'Gangster': 'Gangster',
    'Drugs': 'Drogue',
    'Addiction': 'Addiction',
    'Lawyer': 'Avocat',
    'Vigilante': 'Justicier',
    'Disaster': 'Catastrophe',
    'Volcano': 'Volcan',
    'Tsunami': 'Tsunami',
    'Exploration': 'Exploration',
    'Jungle': 'Jungle',
    'Desert': 'Désert',
    'Arctic': 'Arctique',
    'Ocean': 'Océan',
    'Mystery': 'Mystère',
    'Secret': 'Secret',
    'Music': 'Musique',
    'Dance': 'Danse',
    'Boxing': 'Boxe',
    'Racing': 'Course',
    'Martial arts': 'Arts martiaux',
    'Gym': 'Gymnase',
    'School': 'École',
    'College': 'Lycée',
    'Student': 'Étudiant',
    'Bully': 'Brute',
    'Family': 'Famille',
    'Marriage': 'Mariage',
    'Friendship': 'Amitié',
    'Betrayal': 'Trahison',
    'Redemption': 'Rédemption',
    'Sacrifice': 'Sacrifice',
    'Riot': 'Émeute',
    'Stalker': 'Harceleur',
    'Manhunt': 'Traque',
    'Ambush': 'Embuscade',
    'Hostage': 'Otage',
    'Robbery': 'Vol',
    'Smuggling': 'Contrebande',
    'Chase': 'Poursuite',
    'Hunt': 'Chasse',
    'Hitman': 'Tueur à gages',
    'Assassin': 'Assassin',
    'Blackmail': 'Chantage',
    'Gang': 'Gang',
    'Sabotage': 'Sabotage',
  },
  es: {
    'Shark': 'Tiburón',
    'Ghost': 'Fantasma',
    'Game': 'Juego',
    'Killer': 'Asesino',
    'Revenge': 'Venganza',
    'Space': 'Espacio',
    'Love': 'Amor',
    'Heist': 'Atraco',
    'Camping': 'Campamento',
    'Friends': 'Amigos',
    'Zombie': 'Zombi',
    'Vampire': 'Vampiro',
    'Witch': 'Bruja',
    'Superhero': 'Superhéroe',
    'Villain': 'Villano',
    'Spy': 'Espía',
    'Detective': 'Detective',
    'Prison': 'Prisión',
    'Escape': 'Escape',
    'Survival': 'Supervivencia',
    'Apocalypse': 'Apocalipsis',
    'Dystopia': 'Distopía',
    'Time travel': 'Viaje en el tiempo',
    'Parallel universe': 'Universo paralelo',
    'Alien': 'Extraterrestre',
    'Robot': 'Robot',
    'Nightmare': 'Pesadilla',
    'Mind control': 'Control mental',
    'Time Loop': 'Bucle temporal',
    'Magic': 'Magia',
    'Curse': 'Maldición',
    'Legend': 'Leyenda',
    'Samurai': 'Samurái',
    'Ninja': 'Ninja',
    'Pirate': 'Pirata',
    'War': 'Guerra',
    'Revolution': 'Revolución',
    'Rebellion': 'Rebelión',
    'Conspiracy': 'Conspiración',
    'Mafia': 'Mafia',
    'Gangster': 'Gángster',
    'Drugs': 'Drogas',
    'Addiction': 'Adicción',
    'Lawyer': 'Abogado',
    'Vigilante': 'Vigilante',
    'Disaster': 'Desastre',
    'Volcano': 'Volcán',
    'Tsunami': 'Tsunami',
    'Exploration': 'Exploración',
    'Jungle': 'Selva',
    'Desert': 'Desierto',
    'Arctic': 'Ártico',
    'Ocean': 'Océano',
    'Mystery': 'Misterio',
    'Secret': 'Secreto',
    'Music': 'Música',
    'Dance': 'Baile',
    'Boxing': 'Boxeo',
    'Racing': 'Carreras',
    'Martial arts': 'Artes marciales',
    'Gym': 'Gimnasio',
    'School': 'Escuela',
    'College': 'Colegio',
    'Student': 'Estudiante',
    'Bully': 'Matón',
    'Family': 'Familia',
    'Marriage': 'Matrimonio',
    'Friendship': 'Amistad',
    'Betrayal': 'Traición',
    'Redemption': 'Redención',
    'Sacrifice': 'Sacrificio',
    'Riot': 'Disturbio',
    'Stalker': 'Acosador',
    'Manhunt': 'Cacería',
    'Ambush': 'Emboscada',
    'Hostage': 'Rehén',
    'Robbery': 'Robo',
    'Smuggling': 'Contrabando',
    'Chase': 'Persecución',
    'Hunt': 'Caza',
    'Hitman': 'Sicario',
    'Assassin': 'Asesino',
    'Blackmail': 'Chantaje',
    'Gang': 'Pandilla',
    'Sabotage': 'Sabotaje',
  },
  it: {
    'Shark': 'Squalo',
    'Ghost': 'Fantasma',
    'Game': 'Gioco',
    'Killer': 'Assassino',
    'Revenge': 'Vendetta',
    'Space': 'Spazio',
    'Love': 'Amore',
    'Heist': 'Colpo',
    'Camping': 'Campeggio',
    'Friends': 'Amici',
    'Zombie': 'Zombie',
    'Vampire': 'Vampiro',
    'Witch': 'Strega',
    'Superhero': 'Supereroe',
    'Villain': 'Cattivo',
    'Spy': 'Spia',
    'Detective': 'Detective',
    'Prison': 'Prigione',
    'Escape': 'Fuga',
    'Survival': 'Sopravvivenza',
    'Apocalypse': 'Apocalisse',
    'Dystopia': 'Distopia',
    'Time travel': 'Viaggio nel tempo',
    'Parallel universe': 'Universo parallelo',
    'Alien': 'Alieno',
    'Robot': 'Robot',
    'Nightmare': 'Incubo',
    'Mind control': 'Controllo mentale',
    'Time Loop': 'Ciclo temporale',
    'Magic': 'Magia',
    'Curse': 'Maledizione',
    'Legend': 'Leggenda',
    'Samurai': 'Samurai',
    'Ninja': 'Ninja',
    'Pirate': 'Pirata',
    'War': 'Guerra',
    'Revolution': 'Rivoluzione',
    'Rebellion': 'Ribellione',
    'Conspiracy': 'Cospirazione',
    'Mafia': 'Mafia',
    'Gangster': 'Gangster',
    'Drugs': 'Droga',
    'Addiction': 'Dipendenza',
    'Lawyer': 'Avvocato',
    'Vigilante': 'Giustiziere',
    'Disaster': 'Disastro',
    'Volcano': 'Vulcano',
    'Tsunami': 'Tsunami',
    'Exploration': 'Esplorazione',
    'Jungle': 'Giungla',
    'Desert': 'Deserto',
    'Arctic': 'Artico',
    'Ocean': 'Oceano',
    'Mystery': 'Mistero',
    'Secret': 'Segreto',
    'Music': 'Musica',
    'Dance': 'Danza',
    'Boxing': 'Pugilato',
    'Racing': 'Corse',
    'Martial arts': 'Arti marziali',
    'Gym': 'Palestra',
    'School': 'Scuola',
    'College': 'College',
    'Student': 'Studente',
    'Bully': 'Bullo',
    'Family': 'Famiglia',
    'Marriage': 'Matrimonio',
    'Friendship': 'Amicizia',
    'Betrayal': 'Tradimento',
    'Redemption': 'Redenzione',
    'Sacrifice': 'Sacrificio',
    'Riot': 'Rivolta',
    'Stalker': 'Stalker',
    'Manhunt': 'Caccia all\'uomo',
    'Ambush': 'Agguato',
    'Hostage': 'Ostaggio',
    'Robbery': 'Rapina',
    'Smuggling': 'Contrabbando',
    'Chase': 'Inseguimento',
    'Hunt': 'Caccia',
    'Hitman': 'Sicario',
    'Assassin': 'Assassino',
    'Blackmail': 'Ricatto',
    'Gang': 'Banda',
    'Sabotage': 'Sabotaggio',
  },
  nl: {
    'Shark': 'Haai',
    'Ghost': 'Spook',
    'Game': 'Spel',
    'Killer': 'Moordenaar',
    'Revenge': 'Wraak',
    'Space': 'Ruimte',
    'Love': 'Liefde',
    'Heist': 'Kraak',
    'Camping': 'Kamperen',
    'Friends': 'Vrienden',
    'Zombie': 'Zombie',
    'Vampire': 'Vampier',
    'Witch': 'Heks',
    'Superhero': 'Superheld',
    'Villain': 'Schurk',
    'Spy': 'Spion',
    'Detective': 'Detective',
    'Prison': 'Gevangenis',
    'Escape': 'Ontsnapping',
    'Survival': 'Overleving',
    'Apocalypse': 'Apocalyps',
    'Dystopia': 'Dystopie',
    'Time travel': 'Tijdreizen',
    'Parallel universe': 'Parallel universum',
    'Alien': 'Alien',
    'Robot': 'Robot',
    'Nightmare': 'Nachtmerrie',
    'Mind control': 'Mindcontrol',
    'Time Loop': 'Tijdlus',
    'Magic': 'Magie',
    'Curse': 'Vloek',
    'Legend': 'Legende',
    'Samurai': 'Samurai',
    'Ninja': 'Ninja',
    'Pirate': 'Piraat',
    'War': 'Oorlog',
    'Revolution': 'Revolutie',
    'Rebellion': 'Rebellie',
    'Conspiracy': 'Complot',
    'Mafia': 'Maffia',
    'Gangster': 'Gangster',
    'Drugs': 'Drugs',
    'Addiction': 'Verslaving',
    'Lawyer': 'Advocaat',
    'Vigilante': 'Vigilante',
    'Disaster': 'Ramp',
    'Volcano': 'Vulkaan',
    'Tsunami': 'Tsunami',
    'Exploration': 'Verkenning',
    'Jungle': 'Jungle',
    'Desert': 'Woestijn',
    'Arctic': 'Arctisch',
    'Ocean': 'Oceaan',
    'Mystery': 'Mysterie',
    'Secret': 'Geheim',
    'Music': 'Muziek',
    'Dance': 'Dans',
    'Boxing': 'Boksen',
    'Racing': 'Racen',
    'Martial arts': 'Vechtsport',
    'Gym': 'Sportschool',
    'School': 'School',
    'College': 'College',
    'Student': 'Student',
    'Bully': 'Pestkop',
    'Family': 'Familie',
    'Marriage': 'Huwelijk',
    'Friendship': 'Vriendschap',
    'Betrayal': 'Verraad',
    'Redemption': 'Verlossing',
    'Sacrifice': 'Offer',
    'Riot': 'Rel',
    'Stalker': 'Stalker',
    'Manhunt': 'Mensenjacht',
    'Ambush': 'Hinderlaag',
    'Hostage': 'Gijzelaar',
    'Robbery': 'Overval',
    'Smuggling': 'Smokkel',
    'Chase': 'Achtervolging',
    'Hunt': 'Jacht',
    'Hitman': 'Huurmoordenaar',
    'Assassin': 'Assassin',
    'Blackmail': 'Chantage',
    'Gang': 'Bende',
    'Sabotage': 'Sabotage',
  },
};

// ============================================================
//  Navbar Component
// ============================================================
const Navbar = () => {
  // ----- State -----
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [historyItems, setHistoryItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [selectedMovie, setSelectedMovie] = useState(null);
  const [selectedSeries, setSelectedSeries] = useState(null);
  const [isMovieModalOpen, setIsMovieModalOpen] = useState(false);
  const [isSeriesModalOpen, setIsSeriesModalOpen] = useState(false);
  const [searchMode, setSearchMode] = useState('title');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [requestTimestamps, setRequestTimestamps] = useState([]);
  const [isCooldown, setIsCooldown] = useState(false);
  const [cooldownRemaining, setCooldownRemaining] = useState(0);
  const [noMoreResults, setNoMoreResults] = useState(false);
  const [forbiddenWordWarning, setForbiddenWordWarning] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [shouldBlinkToggle, setShouldBlinkToggle] = useState(false);
  const [hasToggledBefore, setHasToggledBefore] = useState(() =>
    localStorage.getItem('hasToggledSearchMode') === 'true'
  );
  const [showThemeDiscovery, setShowThemeDiscovery] = useState(false);
  const [hasSeenThemeDiscovery, setHasSeenThemeDiscovery] = useState(() =>
    localStorage.getItem('hasSeenThemeDiscovery') === 'true'
  );
  const [showRecommendationPanel, setShowRecommendationPanel] = useState(false);
  const [currentRandomThemes, setCurrentRandomThemes] = useState([]);
  const [isFadingThemes, setIsFadingThemes] = useState(false);

  const [language, setLanguage] = useState(() =>
    localStorage.getItem('preferredLanguage') || 'en'
  );
  const [showGiftModal, setShowGiftModal] = useState(false);
  const [giftCode, setGiftCode] = useState('');

  // ----- Rating format (from localStorage) -----
  const [ratingFormat, setRatingFormat] = useState(() =>
    localStorage.getItem('ratingFormat') || 'stars'
  );

  // ----- Refs -----
  const logoClickCountRef = useRef(0);
  const logoClickTimerRef = useRef(null);
  const detailsCache = useRef({});

  const location = useLocation();
  const navigate = useNavigate();
  const navLinksContainerRef = useRef(null);
  const activeIndicatorRef = useRef(null);
  const navbarContainerRef = useRef(null);
  const scrollAnimationRef = useRef(null);
  const searchInputRef = useRef(null);
  const searchModalRef = useRef(null);
  const historyModalRef = useRef(null);
  const searchResultsContainerRef = useRef(null);
  const cooldownTimerRef = useRef(null);
  const warningTimeoutRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const fadeTimeoutRef = useRef(null);

  const API_KEY = process.env.REACT_APP_TMDB_API_KEY || '57240db50c2008e78c261e1a934627e4';
  const FORBIDDEN_WORDS = ['apple', 'milk'];
  const ALL_THEMES = [
    'Shark', 'Ghost', 'Game', 'Killer', 'Revenge',
    'Space', 'Love', 'Heist', 'Camping', 'Friends',
    'Zombie', 'Vampire', 'Witch',
    'Superhero', 'Villain', 'Spy', 'Detective',
    'Prison', 'Escape', 'Survival', 'Apocalypse', 'Dystopia',
    'Time travel', 'Parallel universe', 'Alien', 'Robot', 'Nightmare', 'Mind control', 'Time Loop',
    'Magic', 'Curse', 'Legend', 'Samurai', 'Ninja', 'Pirate',
    'War', 'Revolution', 'Rebellion', 'Conspiracy', 'Mafia', 'Gangster', 'Drugs', 'Addiction', 'Lawyer', 'Vigilante',
    'Disaster', 'Volcano', 'Tsunami',
    'Exploration', 'Jungle', 'Desert', 'Arctic', 'Ocean',
    'Mystery', 'Secret', 'Music', 'Dance', 'Boxing', 'Racing', 'Martial arts', 'Gym',
    'School', 'College', 'Student', 'Bully',
    'Family', 'Marriage',
    'Friendship', 'Betrayal', 'Redemption', 'Sacrifice',
    'Riot', 'Stalker', 'Manhunt', 'Ambush',
    'Hostage', 'Robbery', 'Smuggling',
    'Chase', 'Hunt', 'Hitman', 'Assassin',
    'Blackmail', 'Gang', 'Sabotage',
  ];
  const THEME_SUGGESTIONS = ALL_THEMES.slice(0, 10);

  // ----- Ad state & helpers (unchanged) -----
  const [overlayVisible, setOverlayVisible] = useState(false);
  const [barVisible, setBarVisible] = useState(false);
  const [barFillProgress, setBarFillProgress] = useState(0);
  const [barText, setBarText] = useState('');
  const [barTextOpacity, setBarTextOpacity] = useState(1);
  const [adCycleClicks, setAdCycleClicks] = useState(0);
  const [adsAllowedByGrace, setAdsAllowedByGrace] = useState(false);
  const [overlayClickable, setOverlayClickable] = useState(true);

  const overlayTimerRef = useRef(null);
  const fillDelayTimerRef = useRef(null);
  const textChangeTimerRef = useRef(null);
  const barHideTimerRef = useRef(null);
  const textFadeTimerRef = useRef(null);
  const visibilityListenerRef = useRef(null);
  const waitingForReturnRef = useRef(false);
  const barAppearTimerRef = useRef(null);
  const clickGraceTimerRef = useRef(null);

  const trimmedCode = giftCode.trim();
  const isCodeValid = ACTIVE_GIFT_CODES.includes(trimmedCode) && !REVOKED_CODES.includes(trimmedCode);

  // ----- Ad helpers (same as before) -----
  const getVisitCount = () => parseInt(localStorage.getItem('ad_visitCount') || '0', 10);
  const setVisitCount = (val) => localStorage.setItem('ad_visitCount', String(val));
  const getLastVisit = () => parseInt(localStorage.getItem('ad_lastVisit') || '0', 10) || null;
  const setLastVisit = (ts) => localStorage.setItem('ad_lastVisit', String(ts));
  const getLastCycleCompletion = () => parseInt(localStorage.getItem('ad_lastCycleCompletion') || '0', 10) || null;
  const setLastCycleCompletion = (ts) => localStorage.setItem('ad_lastCycleCompletion', String(ts));
  const getCycleClicks = () => parseInt(localStorage.getItem('ad_cycleClicks') || '0', 10);
  const setCycleClicks = (val) => localStorage.setItem('ad_cycleClicks', String(val));
  const clearCycleClicks = () => localStorage.removeItem('ad_cycleClicks');

  const computeAdsAllowed = () => {
    const now = Date.now();
    let visitCount = getVisitCount();
    const lastVisit = getLastVisit();
    if (visitCount >= 3) return true;
    const intervalMs = AD_VISIT_INTERVAL_HOURS * 1000;
    const canCount = (lastVisit === null) || (now - lastVisit >= intervalMs);
    if (canCount) {
      visitCount = Math.min(visitCount + 1, 3);
      setVisitCount(visitCount);
      setLastVisit(now);
    }
    return visitCount >= 3;
  };

  const shouldShowAds = () => {
    if (isUserAdFree()) return false;
    if (!adsAllowedByGrace) return false;
    const now = Date.now();
    const lastCompletion = getLastCycleCompletion();
    if (lastCompletion) {
      const hoursSinceCompletion = (now - lastCompletion) / (1000 * 60 * 60);
      if (hoursSinceCompletion < AD_COOLDOWN_HOURS) return false;
    }
    return true;
  };

  const clearAllTimers = () => {
    if (overlayTimerRef.current) clearTimeout(overlayTimerRef.current);
    if (fillDelayTimerRef.current) clearTimeout(fillDelayTimerRef.current);
    if (textChangeTimerRef.current) clearTimeout(textChangeTimerRef.current);
    if (barHideTimerRef.current) clearTimeout(barHideTimerRef.current);
    if (textFadeTimerRef.current) clearTimeout(textFadeTimerRef.current);
    if (visibilityListenerRef.current) {
      document.removeEventListener('visibilitychange', visibilityListenerRef.current);
      visibilityListenerRef.current = null;
    }
    if (barAppearTimerRef.current) clearTimeout(barAppearTimerRef.current);
    if (clickGraceTimerRef.current) clearTimeout(clickGraceTimerRef.current);
    waitingForReturnRef.current = false;
  };

  const scheduleOverlay = (clicks) => {
    if (overlayTimerRef.current) clearTimeout(overlayTimerRef.current);
    if (clicks >= AD_CLICKS_REQUIRED) return;
    overlayTimerRef.current = setTimeout(() => {
      setOverlayVisible(true);
    }, AD_SHOW_DELAY_SECONDS * 1000);
  };

  const startAdCycle = () => {
    if (!shouldShowAds()) return;
    const existingClicks = getCycleClicks();
    if (existingClicks >= AD_CLICKS_REQUIRED) {
      clearCycleClicks();
      setCycleClicks(0);
      setAdCycleClicks(0);
      return;
    }
    setAdCycleClicks(existingClicks);
    setBarTextOpacity(1);
    setBarFillProgress(existingClicks / AD_CLICKS_REQUIRED);
    setBarText(`${existingClicks}/${AD_CLICKS_REQUIRED} Until Ad-Free`);
    if (barAppearTimerRef.current) clearTimeout(barAppearTimerRef.current);
    barAppearTimerRef.current = setTimeout(() => {
      setBarVisible(true);
    }, AD_BAR_APPEAR_DELAY_SECONDS * 1000);
    scheduleOverlay(existingClicks);
  };

  const applyBarUpdate = (newClicks) => {
    setBarTextOpacity(1);
    setBarFillProgress(newClicks / AD_CLICKS_REQUIRED);
    setBarText(`${newClicks}/${AD_CLICKS_REQUIRED} Until Ad-Free`);
    if (newClicks >= AD_CLICKS_REQUIRED) {
      setLastCycleCompletion(Date.now());
      if (textChangeTimerRef.current) clearTimeout(textChangeTimerRef.current);
      textChangeTimerRef.current = setTimeout(() => {
        setBarTextOpacity(0);
        if (textFadeTimerRef.current) clearTimeout(textFadeTimerRef.current);
        textFadeTimerRef.current = setTimeout(() => {
          setBarText('Ad-Free Experience Ahead. Have Fun!');
          setBarTextOpacity(1);
          if (barHideTimerRef.current) clearTimeout(barHideTimerRef.current);
          barHideTimerRef.current = setTimeout(() => {
            setBarVisible(false);
            setTimeout(() => {
              setBarFillProgress(0);
              setBarText('');
              setBarTextOpacity(1);
              clearCycleClicks();
              setAdCycleClicks(0);
            }, 500);
          }, AD_FREE_MESSAGE_DURATION_SECONDS * 1000);
        }, 300);
      }, AD_COMPLETED_MESSAGE_DELAY_SECONDS * 1000);
    }
  };

  const waitForReturnThenUpdate = (newClicks) => {
    if (visibilityListenerRef.current) {
      document.removeEventListener('visibilitychange', visibilityListenerRef.current);
      visibilityListenerRef.current = null;
    }
    waitingForReturnRef.current = true;
    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && waitingForReturnRef.current) {
        waitingForReturnRef.current = false;
        document.removeEventListener('visibilitychange', handleVisibility);
        visibilityListenerRef.current = null;
        if (newClicks < AD_CLICKS_REQUIRED) {
          scheduleOverlay(newClicks);
        }
        fillDelayTimerRef.current = setTimeout(() => {
          applyBarUpdate(newClicks);
        }, AD_RETURN_UPDATE_DELAY_SECONDS * 1000);
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    visibilityListenerRef.current = handleVisibility;
  };

  const handleOverlayClick = () => {
    if (!overlayClickable) return;
    const smartLink = 'https://amrox.org/4/a15e5a4291b030aa61dbb4b98c952698';
    if (smartLink) window.open(smartLink, '_blank');
    setOverlayVisible(false);
    const newClicks = adCycleClicks + 1;
    setAdCycleClicks(newClicks);
    setCycleClicks(newClicks);
    waitForReturnThenUpdate(newClicks);
  };

  const isWatchPath = AD_WATCH_PATHS.some(path => location.pathname.startsWith(path));

  // ----- Helper functions -----
  const t = (key) => {
    const langData = translations[language] || translations.en;
    return langData[key] || key;
  };
  const getThemeDisplay = (themeEn) => {
    const langMap = themeTranslations[language] || themeTranslations.en;
    return langMap[themeEn] || themeEn;
  };

  // ----- Check forbidden words -----
  const checkForForbiddenWords = useCallback((query) => {
    const words = query.toLowerCase().split(/\s+/);
    const found = FORBIDDEN_WORDS.find(w => words.includes(w));
    if (found) {
      const cleaned = query.replace(new RegExp(`\\b${found}\\b`, 'gi'), '').trim();
      setSearchQuery(cleaned);
      setForbiddenWordWarning(`The word "${found}" is not allowed.`);
      if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
      warningTimeoutRef.current = setTimeout(() => setForbiddenWordWarning(''), 3000);
      return true;
    }
    return false;
  }, []);

  // ----- Generate random themes -----
  const generateRandomThemes = useCallback(() => {
    const shuffled = [...ALL_THEMES].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, 10);
  }, []);

  useEffect(() => {
    if (currentRandomThemes.length === 0) {
      setCurrentRandomThemes(generateRandomThemes());
    }
  }, [generateRandomThemes, currentRandomThemes.length]);

  // ----- Search input handler -----
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    if (value.trim() === '') {
      if (searchMode === 'keyword' && !hasSeenThemeDiscovery) setShowThemeDiscovery(true);
    } else {
      setShowThemeDiscovery(false);
    }
    setIsTyping(true);
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    if (value.trim()) checkForForbiddenWords(value);
    typingTimeoutRef.current = setTimeout(() => setIsTyping(false), 500);
  };

  const clearSearchInput = () => {
    setSearchQuery('');
    setSearchResults([]);
    setCurrentPage(1);
    setTotalPages(0);
    setNoMoreResults(false);
    if (searchMode === 'keyword' && !hasSeenThemeDiscovery) setShowThemeDiscovery(true);
    if (searchInputRef.current) searchInputRef.current.focus();
  };

  // ----- History -----
  useEffect(() => {
    const saved = localStorage.getItem('viewingHistory');
    if (saved) setHistoryItems(JSON.parse(saved));
    return () => {
      if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      if (fadeTimeoutRef.current) clearTimeout(fadeTimeoutRef.current);
    };
  }, []);

  // ----- addToHistory (stores full metadata) -----
  const addToHistory = useCallback((item) => {
    const isMovie = item.media_type === 'movie' || (!item.media_type && item.title);
    const isSeries = item.media_type === 'tv' || (!item.media_type && item.name);
    let release_year = null, runtime = null;
    let first_air_year = null, last_air_year = null, number_of_seasons = null;

    if (isMovie) {
      release_year = item.release_year || (item.release_date ? new Date(item.release_date).getFullYear() : null);
      runtime = item.runtime || 0;
    } else if (isSeries) {
      first_air_year = item.first_air_year || (item.first_air_date ? new Date(item.first_air_date).getFullYear() : null);
      last_air_year = item.last_air_year || (item.last_air_date ? new Date(item.last_air_date).getFullYear() : null);
      number_of_seasons = item.number_of_seasons || 0;
    }

    const newItem = {
      id: item.id,
      title: item.title || item.name || '',
      poster_path: item.poster_path || '',
      media_type: item.media_type || (isMovie ? 'movie' : 'tv'),
      vote_average: item.vote_average || 0,
      release_date: item.release_date || item.first_air_date || '',
      overview: item.overview || '',
      genre_ids: item.genre_ids || [],
      backdrop_path: item.backdrop_path || '',
      vote_count: item.vote_count || 0,
      original_title: item.original_title || item.original_name || '',
      original_language: item.original_language || 'en',
      release_year,
      runtime,
      first_air_year,
      last_air_year,
      number_of_seasons,
    };

    setHistoryItems(prev => {
      const filtered = prev.filter(h => !(h.id === newItem.id && h.media_type === newItem.media_type));
      const updated = [newItem, ...filtered].slice(0, 21);
      localStorage.setItem('viewingHistory', JSON.stringify(updated));
      return updated;
    });
  }, []);

  const removeFromHistory = useCallback((itemToRemove) => {
    setHistoryItems(prev => {
      const updated = prev.filter(item => !(item.id === itemToRemove.id && item.media_type === itemToRemove.media_type));
      localStorage.setItem('viewingHistory', JSON.stringify(updated));
      return updated;
    });
  }, []);

  // ----- Enrich series (cached) -----
  const enrichSeries = useCallback(async (item) => {
    if (item.media_type !== 'tv') return item;
    const cacheKey = `tv-${item.id}`;
    if (detailsCache.current[cacheKey]) {
      return { ...item, ...detailsCache.current[cacheKey] };
    }
    try {
      const details = await fetchSeriesDetails(item.id);
      const enriched = {
        number_of_seasons: details.number_of_seasons || 0,
        first_air_year: details.first_air_date ? new Date(details.first_air_date).getFullYear() : null,
        last_air_year: details.last_air_date ? new Date(details.last_air_date).getFullYear() : null,
      };
      detailsCache.current[cacheKey] = enriched;
      return { ...item, ...enriched };
    } catch (e) {
      console.warn('Failed to enrich series', item.id, e);
      return item;
    }
  }, []);

  // ----- Listen for events from modals (with enrichment) -----
  useEffect(() => {
    const handleMovieOpened = (e) => {
      addToHistory(e.detail);
    };
    const handleSeriesOpened = async (e) => {
      let item = e.detail;
      if (item.media_type === 'tv' && (!item.first_air_year || !item.last_air_year || !item.number_of_seasons)) {
        item = await enrichSeries(item);
      }
      addToHistory(item);
    };
    window.addEventListener('movieOpened', handleMovieOpened);
    window.addEventListener('seriesOpened', handleSeriesOpened);
    return () => {
      window.removeEventListener('movieOpened', handleMovieOpened);
      window.removeEventListener('seriesOpened', handleSeriesOpened);
    };
  }, [addToHistory, enrichSeries]);

  // Language sync across tabs
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'preferredLanguage') setLanguage(e.newValue || 'en');
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Rating format sync across tabs
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'ratingFormat') setRatingFormat(e.newValue || 'stars');
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Scroll to center (mobile)
  const scrollToCenter = useCallback((element) => {
    if (!navLinksContainerRef.current) return;
    const container = navLinksContainerRef.current;
    const containerRect = container.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();
    const containerCenter = containerRect.width / 2;
    const elementCenter = elementRect.left - containerRect.left + elementRect.width / 2;
    const scrollPos = container.scrollLeft + elementCenter - containerCenter;
    const startScroll = container.scrollLeft;
    const distance = scrollPos - startScroll;
    const duration = 500;
    let startTime = null;
    if (scrollAnimationRef.current) cancelAnimationFrame(scrollAnimationRef.current);
    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const elapsed = timestamp - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = progress < 0.5 ? 4 * progress * progress * progress : 1 - Math.pow(-2 * progress + 2, 3) / 2;
      container.scrollLeft = startScroll + distance * eased;
      if (progress < 1) scrollAnimationRef.current = requestAnimationFrame(animate);
      else scrollAnimationRef.current = null;
    };
    scrollAnimationRef.current = requestAnimationFrame(animate);
  }, []);

  // ----- Smooth active indicator update -----
  const updateActiveIndicator = useCallback(() => {
    if (!activeIndicatorRef.current || !navLinksContainerRef.current) return;
    const activeLink = document.querySelector('.nav-link.active');
    if (!activeLink) return;
    const container = navLinksContainerRef.current;
    const linkRect = activeLink.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    const textWidth = activeLink.scrollWidth;
    activeIndicatorRef.current.style.width = `${textWidth}px`;
    const relativeLeft = linkRect.left - containerRect.left + container.scrollLeft;
    const centerPos = relativeLeft + linkRect.width / 2;
    activeIndicatorRef.current.style.left = `${centerPos}px`;
    activeIndicatorRef.current.style.transform = 'translateX(-50%)';
    if (window.innerWidth <= 768) scrollToCenter(activeLink);
  }, [scrollToCenter]);

  // Trigger indicator update after location change (with a small delay for smoothness)
  useEffect(() => {
    const timer = setTimeout(() => {
      requestAnimationFrame(() => {
        updateActiveIndicator();
      });
    }, 50);
    return () => clearTimeout(timer);
  }, [location, updateActiveIndicator]);

  // Toggle search mode
  const toggleSearchMode = (mode) => {
    if (mode === searchMode) return;
    if (shouldBlinkToggle) setShouldBlinkToggle(false);
    if (!hasToggledBefore) {
      localStorage.setItem('hasToggledSearchMode', 'true');
      setHasToggledBefore(true);
    }
    setSearchMode(mode);
    if (mode === 'keyword' && !hasSeenThemeDiscovery && searchQuery === '') {
      setShowThemeDiscovery(true);
    } else {
      setShowThemeDiscovery(false);
    }
    setSearchQuery('');
    setSearchResults([]);
    setCurrentPage(1);
    setTotalPages(0);
    setNoMoreResults(false);
  };

  const dismissThemeDiscovery = () => {
    setShowThemeDiscovery(false);
    localStorage.setItem('hasSeenThemeDiscovery', 'true');
    setHasSeenThemeDiscovery(true);
  };

  const handleThemeClick = (themeEn) => {
    setSearchQuery(themeEn);
    setShowThemeDiscovery(false);
  };

  const handleRecommendationThemeClick = (themeEn) => {
    setSearchQuery(themeEn);
    setIsTyping(true);
  };

  const handleRecommendClick = () => setShowRecommendationPanel(true);
  const handleMorePlease = () => {
    if (isFadingThemes) return;
    setIsFadingThemes(true);
    fadeTimeoutRef.current = setTimeout(() => {
      setCurrentRandomThemes(generateRandomThemes());
      setIsFadingThemes(false);
    }, 200);
  };

  useEffect(() => {
    if (searchMode === 'title') setShowRecommendationPanel(false);
  }, [searchMode]);

  // ----- Fetch search results (with enrichment) -----
  const fetchSearchResults = useCallback(async (query, page = 1, mode = searchMode) => {
    if (!query.trim() || checkForForbiddenWords(query)) {
      setSearchResults([]);
      setIsTyping(false);
      return { results: [], totalPages: 0 };
    }
    const loading = page === 1 ? setIsLoading : setIsLoadingMore;
    loading(true);
    try {
      let url;
      if (mode === 'title') {
        url = `https://api.themoviedb.org/3/search/multi?api_key=${API_KEY}&language=en-US&query=${encodeURIComponent(query)}&page=${page}&include_adult=false`;
      } else {
        const keywordRes = await fetch(
          `https://api.themoviedb.org/3/search/keyword?api_key=${API_KEY}&query=${encodeURIComponent(query)}&page=1`
        );
        if (!keywordRes.ok) throw new Error('Failed to fetch keywords');
        const keywordData = await keywordRes.json();
        if (keywordData.results.length === 0) {
          setSearchResults([]);
          loading(false);
          setIsTyping(false);
          return { results: [], totalPages: 0 };
        }
        const keywordId = keywordData.results[0].id;
        url = `https://api.themoviedb.org/3/discover/movie?api_key=${API_KEY}&with_keywords=${keywordId}&page=${page}&include_adult=false`;
      }

      const response = await fetch(url);
      if (!response.ok) throw new Error('Failed to fetch search results');
      const data = await response.json();

      let filtered = data.results.filter(
        item => (item.media_type === 'movie' || item.media_type === 'tv' || mode === 'keyword') &&
                item.poster_path &&
                (item.vote_count || 0) >= 30
      );
      if (mode === 'keyword') {
        filtered.forEach(item => { item.media_type = 'movie'; });
      }

      // Enrich with details
      const enriched = await Promise.all(
        filtered.map(async (item) => {
          const cacheKey = `${item.media_type}-${item.id}`;
          if (detailsCache.current[cacheKey]) {
            return { ...item, ...detailsCache.current[cacheKey] };
          }
          try {
            if (item.media_type === 'movie') {
              const details = await fetchMovieDetails(item.id);
              const enrichedItem = {
                ...item,
                release_year: details.release_date ? new Date(details.release_date).getFullYear() : null,
                runtime: details.runtime || 0,
              };
              detailsCache.current[cacheKey] = {
                release_year: enrichedItem.release_year,
                runtime: enrichedItem.runtime,
              };
              return enrichedItem;
            } else if (item.media_type === 'tv') {
              const details = await fetchSeriesDetails(item.id);
              const enrichedItem = {
                ...item,
                number_of_seasons: details.number_of_seasons || 0,
                first_air_year: details.first_air_date ? new Date(details.first_air_date).getFullYear() : null,
                last_air_year: details.last_air_date ? new Date(details.last_air_date).getFullYear() : null,
              };
              detailsCache.current[cacheKey] = {
                number_of_seasons: enrichedItem.number_of_seasons,
                first_air_year: enrichedItem.first_air_year,
                last_air_year: enrichedItem.last_air_year,
              };
              return enrichedItem;
            }
            return item;
          } catch (e) {
            console.warn('Failed to enrich', item.id, e);
            return item;
          }
        })
      );

      return { results: enriched, totalPages: data.total_pages };
    } catch (error) {
      console.error('Search error:', error);
      return { results: [], totalPages: 0 };
    } finally {
      loading(false);
      setIsTyping(false);
    }
  }, [API_KEY, searchMode, checkForForbiddenWords]);

  // Debounced search
  useEffect(() => {
    if (!isSearchOpen) return;
    const handler = setTimeout(async () => {
      setNoMoreResults(false);
      const { results, totalPages } = await fetchSearchResults(searchQuery, 1);
      setSearchResults(results);
      setTotalPages(totalPages);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchQuery, isSearchOpen, fetchSearchResults]);

  // Load more
  const loadMoreResults = async () => {
    if (isCooldown || noMoreResults) return;
    const now = Date.now();
    const tenSecondsAgo = now - 10000;
    const recent = requestTimestamps.filter(ts => ts > tenSecondsAgo);
    const updated = [...recent, now];
    setRequestTimestamps(updated);
    if (updated.length >= 22) {
      setIsCooldown(true);
      setCooldownRemaining(8);
      if (cooldownTimerRef.current) clearInterval(cooldownTimerRef.current);
      cooldownTimerRef.current = setInterval(() => {
        setCooldownRemaining(prev => {
          if (prev <= 1) {
            clearInterval(cooldownTimerRef.current);
            setIsCooldown(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return;
    }
    if (currentPage < totalPages && !isLoadingMore) {
      setIsLoadingMore(true);
      let nextPage = currentPage + 1;
      let attempts = 0;
      let found = false;
      while (attempts < 4 && nextPage <= totalPages && !found) {
        const { results } = await fetchSearchResults(searchQuery, nextPage);
        if (results.length > 0) {
          setSearchResults(prev => [...prev, ...results]);
          setCurrentPage(nextPage);
          found = true;
        } else {
          nextPage++;
          attempts++;
        }
      }
      setIsLoadingMore(false);
      if (!found && (nextPage > totalPages || attempts >= 4)) setNoMoreResults(true);
    } else if (currentPage >= totalPages) {
      setNoMoreResults(true);
    }
  };

  // ----- Open/close modals (fixed indicator handling) -----
  const openSearchModal = () => {
    setIsSearchOpen(true);
    setSearchQuery('');
    setSearchResults([]);
    setCurrentPage(1);
    setTotalPages(0);
    setNoMoreResults(false);
    if (searchMode === 'keyword' && !hasSeenThemeDiscovery) setShowThemeDiscovery(true);
    if (!hasToggledBefore) setShouldBlinkToggle(true);
    if (isHistoryOpen) closeHistoryModal();
    document.body.classList.add('no-scroll');
    setTimeout(() => {
      if (searchInputRef.current) searchInputRef.current.focus();
    }, 100);
  };

  const closeSearchModal = () => {
    setIsSearchOpen(false);
    setSearchQuery('');
    setSearchResults([]);
    setCurrentPage(1);
    setTotalPages(0);
    setNoMoreResults(false);
    setShowThemeDiscovery(false);
    document.body.classList.remove('no-scroll');
    setTimeout(() => {
      requestAnimationFrame(updateActiveIndicator);
    }, 50);
  };

  const openHistoryModal = () => {
    setIsHistoryOpen(true);
    if (isSearchOpen) closeSearchModal();
    document.body.classList.add('no-scroll');
  };

  const closeHistoryModal = () => {
    setIsHistoryOpen(false);
    document.body.classList.remove('no-scroll');
    setTimeout(() => {
      requestAnimationFrame(updateActiveIndicator);
    }, 50);
  };

  // ----- Handler for navigation links: close modals -----
  const handleNavLinkClick = () => {
    if (isSearchOpen) closeSearchModal();
    if (isHistoryOpen) closeHistoryModal();
  };

  // ----- Handle item click (enrich series if needed) -----
  const handleItemClick = async (item) => {
    let enrichedItem = { ...item };
    if (item.media_type === 'tv' && (!item.first_air_year || !item.last_air_year || !item.number_of_seasons)) {
      enrichedItem = await enrichSeries(item);
    }
    addToHistory(enrichedItem);
    if (enrichedItem.media_type === 'movie') {
      try {
        const response = await fetch(`https://api.themoviedb.org/3/movie/${enrichedItem.id}?api_key=${API_KEY}&language=en-US`);
        if (response.ok) {
          const details = await response.json();
          setSelectedMovie(details);
        } else {
          setSelectedMovie(enrichedItem);
        }
      } catch (error) {
        console.error('Error fetching movie details:', error);
        setSelectedMovie(enrichedItem);
      }
      setIsMovieModalOpen(true);
    } else if (enrichedItem.media_type === 'tv') {
      setSelectedSeries(enrichedItem);
      setIsSeriesModalOpen(true);
    }
  };

  const closeMovieModal = () => {
    setIsMovieModalOpen(false);
    setSelectedMovie(null);
  };
  const closeSeriesModal = () => {
    setIsSeriesModalOpen(false);
    setSelectedSeries(null);
  };

  // Navbar scroll effect
  useEffect(() => {
    const handleResize = () => {
      requestAnimationFrame(updateActiveIndicator);
    };
    const handleScroll = () => {
      if (!navbarContainerRef.current) return;
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const navbar = document.querySelector('.navbar');
      const navbarHeight = navbar.offsetHeight;
      const container = navbarContainerRef.current;
      const threshold = window.innerHeight * 0.2;
      if (scrollTop <= threshold) {
        container.style.transform = 'translateY(0)';
      } else if (scrollTop > 100) {
        container.style.transform = `translateY(-${navbarHeight * 0.5}px)`;
      }
    };
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll);
      if (scrollAnimationRef.current) cancelAnimationFrame(scrollAnimationRef.current);
      if (cooldownTimerRef.current) clearInterval(cooldownTimerRef.current);
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      if (fadeTimeoutRef.current) clearTimeout(fadeTimeoutRef.current);
    };
  }, [updateActiveIndicator]);

  // AD effects
  useEffect(() => {
    const allowed = computeAdsAllowed();
    setAdsAllowedByGrace(allowed);
    return () => clearAllTimers();
  }, []);

  useEffect(() => {
    if (adsAllowedByGrace && shouldShowAds()) startAdCycle();
  }, [adsAllowedByGrace]);

  useEffect(() => {
    if (isWatchPath) {
      setOverlayClickable(false);
      if (clickGraceTimerRef.current) clearTimeout(clickGraceTimerRef.current);
    } else {
      if (clickGraceTimerRef.current) clearTimeout(clickGraceTimerRef.current);
      clickGraceTimerRef.current = setTimeout(() => setOverlayClickable(true), AD_WATCH_PATH_GRACE_SECONDS * 1000);
    }
    return () => { if (clickGraceTimerRef.current) clearTimeout(clickGraceTimerRef.current); };
  }, [isWatchPath]);

  // Logo click (gift code)
  const handleLogoClick = () => {
    if (logoClickTimerRef.current) {
      logoClickCountRef.current += 1;
    } else {
      logoClickCountRef.current = 1;
      logoClickTimerRef.current = setTimeout(() => {
        logoClickCountRef.current = 0;
        logoClickTimerRef.current = null;
      }, 10000);
    }
    if (logoClickCountRef.current >= 5) {
      logoClickCountRef.current = 0;
      if (logoClickTimerRef.current) {
        clearTimeout(logoClickTimerRef.current);
        logoClickTimerRef.current = null;
      }
      setGiftCode('');
      setShowGiftModal(true);
    }
  };

  const handleRedeem = () => {
    if (!isCodeValid) return;
    localStorage.setItem('adFreeCode', trimmedCode);
    setShowGiftModal(false);
    setGiftCode('');
  };

  const showAds = !isUserAdFree();

  // ============================================================
  //  RENDER
  // ============================================================
  // Compute whether extra content (theme discovery / recommendation) is visible
  const isExtraVisible = searchMode === 'keyword' && searchQuery === '';

  return (
    <>
      {/* Search Modal */}
      <div ref={searchModalRef} className={`search-modal ${isSearchOpen ? 'open' : ''}`}>
        <div className="search-header">
          <div className={`search-input-container ${searchMode === 'keyword' ? 'keyword-mode' : ''}`}>
            <input
              ref={searchInputRef}
              type="text"
              placeholder={searchMode === 'title' ? t('searchPlaceholderTitle') : t('searchPlaceholderKeyword')}
              value={searchQuery}
              onChange={handleSearchChange}
              autoFocus
            />
            {searchQuery && (
              <button className="clear-search-btn" onClick={clearSearchInput} aria-label={t('clearSearchAria')}>
                <i className="fas fa-close"></i>
              </button>
            )}
            <div className="toggle-switch-container">
              <div className={`toggle-option ${searchMode === 'title' ? 'active' : ''}`} onClick={() => toggleSearchMode('title')}>
                {t('titleMode')}
              </div>
              <div className={`toggle-option ${searchMode === 'keyword' ? 'active' : ''}`} onClick={() => toggleSearchMode('keyword')}>
                {t('themeMode')}
              </div>
              <div className="toggle-slider" style={{ transform: searchMode === 'title' ? 'translateX(0)' : 'translateX(100%)' }} />
            </div>

            {/* Glued extra content: Theme Discovery or Recommendation */}
            {isExtraVisible && (
              <div className="search-extra">
                {showThemeDiscovery ? (
                  // Theme Discovery
                  <div className="theme-discovery-container">
                    <div className="theme-discovery-title">{t('themeDiscoveryTitle')}</div>
                    <div className="theme-discovery-subtitle">{t('themeDiscoverySubtitle')}</div>
                    <div className="theme-suggestions-grid">
                      {(typeof window !== 'undefined' && !hasSeenThemeDiscovery && window.innerWidth <= 768
                        ? THEME_SUGGESTIONS.slice(0, 6)
                        : THEME_SUGGESTIONS
                      ).map((themeEn) => {
                        const display = getThemeDisplay(themeEn);
                        return (
                          <div key={themeEn} className="theme-chip" onClick={() => handleThemeClick(themeEn)}>
                            {display}
                          </div>
                        );
                      })}
                    </div>
                    <button className="theme-got-it-btn" onClick={dismissThemeDiscovery}>
                      {t('gotItBtn')}
                    </button>
                  </div>
                ) : hasSeenThemeDiscovery ? (
                  // Recommendation area
                  <>
                    {!showRecommendationPanel ? (
                      <div className="recommend-btn-wrapper">
                        <button className="recommend-btn" onClick={handleRecommendClick}>
                          {t('recommendBtn')}
                        </button>
                      </div>
                    ) : (
                      <div className="recommendation-panel">
                        <div className={`recommendation-grid ${isFadingThemes ? 'fade' : ''}`}>
                          {currentRandomThemes.map((themeEn) => {
                            const display = getThemeDisplay(themeEn);
                            return (
                              <div key={themeEn} className="recommendation-chip" onClick={() => handleRecommendationThemeClick(themeEn)}>
                                {display}
                              </div>
                            );
                          })}
                        </div>
                        <button className="more-please-btn" onClick={handleMorePlease}>{t('somethingElseBtn')}</button>
                      </div>
                    )}
                  </>
                ) : null}
              </div>
            )}
          </div>
        </div>

        {forbiddenWordWarning && (
          <div className="forbidden-word-warning">{forbiddenWordWarning}</div>
        )}

        {/* Search Results */}
        <div className={`search-results ${isExtraVisible ? 'has-extra' : ''}`} ref={searchResultsContainerRef}>
          {isLoading || isTyping ? (
            <div className="loading-indicator">{t('loadingIndicator')}</div>
          ) : (
            <>
              {searchQuery && searchResults.length > 0 ? (
                <>
                  <div className="results-grid">
                    {searchResults.map((item) => {
                      if (item.media_type === 'movie') {
                        return (
                          <MovieCard
                            key={`movie-${item.id}`}
                            movie={item}
                            onClick={() => handleItemClick(item)}
                            releaseYear={item.release_year}
                            runtime={item.runtime}
                            ratingFormat={ratingFormat}
                          />
                        );
                      } else if (item.media_type === 'tv') {
                        return (
                          <SeriesCard
                            key={`tv-${item.id}`}
                            series={item}
                            onSeriesClick={() => handleItemClick(item)}
                            firstAirYear={item.first_air_year}
                            lastAirYear={item.last_air_year}
                            numberOfSeasons={item.number_of_seasons}
                            ratingFormat={ratingFormat}
                          />
                        );
                      }
                      return null;
                    })}
                  </div>
                  {currentPage < totalPages && !noMoreResults && (
                    <div className="load-more-container">
                      <button className="load-more-btn" onClick={loadMoreResults} disabled={isLoadingMore || isCooldown}>
                        {isLoadingMore ? (
                          <><i className="fas fa-spinner fa-spin"></i> {t('loadingIndicator')}</>
                        ) : isCooldown ? (
                          t('cooldownLabel').replace('{seconds}', cooldownRemaining)
                        ) : (
                          t('showMore')
                        )}
                      </button>
                    </div>
                  )}
                  {noMoreResults && <div className="no-more-results">{t('noMoreResults')}</div>}
                </>
              ) : searchQuery ? (
                <div className="no-results">{searchMode === 'title' ? t('noResultsTitle') : t('noResultsKeyword')}</div>
              ) : null}
            </>
          )}
        </div>
      </div>

      {/* History Modal */}
      <div ref={historyModalRef} className={`search-modal history-modal ${isHistoryOpen ? 'open' : ''}`}>
        <div className="search-header">
          <div className="history-title">{t('historyTitle')}</div>
        </div>
        <div className="search-results">
          {historyItems.length > 0 ? (
            <div className="results-grid">
              {historyItems.map((item) => {
                if (item.media_type === 'movie') {
                  return (
                    <div key={`movie-${item.id}`} className="history-item-wrapper">
                      <MovieCard
                        movie={item}
                        onClick={() => handleItemClick(item)}
                        releaseYear={item.release_year}
                        runtime={item.runtime}
                        ratingFormat={ratingFormat}
                      />
                      <button
                        className="remove-history-btn"
                        onClick={(e) => { e.stopPropagation(); removeFromHistory(item); }}
                        aria-label={t('removeFromHistoryAria')}
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    </div>
                  );
                } else if (item.media_type === 'tv') {
                  return (
                    <div key={`tv-${item.id}`} className="history-item-wrapper">
                      <SeriesCard
                        series={item}
                        onSeriesClick={() => handleItemClick(item)}
                        firstAirYear={item.first_air_year}
                        lastAirYear={item.last_air_year}
                        numberOfSeasons={item.number_of_seasons}
                        ratingFormat={ratingFormat}
                      />
                      <button
                        className="remove-history-btn"
                        onClick={(e) => { e.stopPropagation(); removeFromHistory(item); }}
                        aria-label={t('removeFromHistoryAria')}
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    </div>
                  );
                }
                return null;
              })}
            </div>
          ) : (
            <div className="no-results">{t('historyEmpty')}</div>
          )}
        </div>
      </div>

      {/* Movie Modal */}
      {isMovieModalOpen && selectedMovie && (
        <MoviesModal movie={selectedMovie} onClose={closeMovieModal} onUpdate={() => {}} isInSearchContext={true} />
      )}

      {/* Series Modal */}
      {isSeriesModalOpen && selectedSeries && (
        <SeriesModal isOpen={true} onClose={closeSeriesModal} seriesId={selectedSeries.id} onUpdate={() => {}} isInSearchContext={true} />
      )}

      {/* Navbar */}
      <div className="navbar-container" id="navbarContainer" ref={navbarContainerRef}>
        <nav className="navbar">
          <div className="top-bar">
            <div className={`nav-icon heart-btn ${isHistoryOpen ? 'active' : ''}`} onClick={isHistoryOpen ? closeHistoryModal : openHistoryModal}>
              <i className="fas fa-clock-rotate-left"></i>
              <i className="fas fa-times"></i>
            </div>
            <div className="logo" onClick={handleLogoClick}>
              <span className="logo-text">STREAM4CITY</span>
              <img className="logo-image" src="/stream4logo3.png" alt="STREAM4CITY" />
            </div>
            <div className={`nav-icon search-btn ${isSearchOpen ? 'active' : ''}`} onClick={isSearchOpen ? closeSearchModal : openSearchModal}>
              <i className="fas fa-search"></i>
              <i className="fas fa-times"></i>
            </div>
          </div>
          <div className="nav-links-container" id="navLinksContainer" ref={navLinksContainerRef}>
            <ul className="nav-links" id="navLinks">
              <li className="nav-item">
                <NavLink to="/" className="nav-link" activeClassName="active" exact onClick={handleNavLinkClick}>
                  {t('recommended')}
                </NavLink>
              </li>
              <li className="nav-item">
                <NavLink to="/trending" className="nav-link" activeClassName="active" onClick={handleNavLinkClick}>
                  {t('trending')}
                </NavLink>
              </li>
              <li className="nav-item">
                <NavLink to="/movies" className="nav-link" activeClassName="active" onClick={handleNavLinkClick}>
                  {t('movies')}
                </NavLink>
              </li>
              <li className="nav-item">
                <NavLink to="/series" className="nav-link" activeClassName="active" onClick={handleNavLinkClick}>
                  {t('series')}
                </NavLink>
              </li>
              <li className="nav-item">
                <NavLink to="/actors" className="nav-link" activeClassName="active" onClick={handleNavLinkClick}>
                  {t('actors')}
                </NavLink>
              </li>
              <li className="nav-item">
                <NavLink to="/favorites" className="nav-link" activeClassName="active" onClick={handleNavLinkClick}>
                  {t('myList')}
                </NavLink>
              </li>
            </ul>
            <div className={`active-indicator ${isSearchOpen || isHistoryOpen ? 'fade-out' : ''}`} id="activeIndicator" ref={activeIndicatorRef}></div>
          </div>
          <div className="static-line-container"><div className="static-line"></div></div>
        </nav>
      </div>

      {/* Gift Code Modal */}
      {showGiftModal && (
        <div className="user-id-popup-overlay">
          <div className="user-id-popup">
            <button className="user-id-close-btn" onClick={() => setShowGiftModal(false)}><i className="fas fa-times"></i></button>
            <div className="user-id-title">Redeem Gift</div>
            <div style={{ marginBottom: '20px' }}>
              <input
                type="text"
                className="gift-input"
                value={giftCode}
                onChange={(e) => setGiftCode(e.target.value.toUpperCase())}
                placeholder="Your Code"
                autoFocus
                spellCheck={false}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  fontSize: '1.8rem',
                  fontFamily: 'Courier New, monospace',
                  fontWeight: 'bold',
                  letterSpacing: '2px',
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: '12px',
                  color: '#fff',
                  textAlign: 'center',
                  outline: 'none',
                  textTransform: 'uppercase'
                }}
              />
            </div>
            <button
              className={`user-id-copy-btn ${isCodeValid ? 'valid' : ''}`}
              onClick={handleRedeem}
              disabled={!isCodeValid}
              style={{
                background: isCodeValid ? 'linear-gradient(135deg, #59f001, #b8962e)' : '#555',
                color: isCodeValid ? '#111' : '#999',
                cursor: isCodeValid ? 'pointer' : 'not-allowed',
                border: 'none',
                padding: '12px 36px',
                borderRadius: '40px',
                fontSize: '1rem',
                fontWeight: '600',
                transition: 'all 0.3s ease',
                boxShadow: isCodeValid ? '0 6px 24px rgba(212, 175, 55, 0.3)' : 'none',
                width: '100%',
                marginTop: '8px'
              }}
            >
              Redeem
            </button>
            <div style={{ marginTop: '16px', fontSize: '1rem', fontWeight: '500', letterSpacing: '0.5px', color: '#59f001', opacity: isCodeValid ? 1 : 0, transition: 'opacity 0.4s ease', pointerEvents: 'none' }}>
              Ad‑free, forever.
            </div>
            <div style={{ marginTop: '4px', fontSize: '0.8rem', fontWeight: '300', letterSpacing: '0.5px', color: '#ffffff36', opacity: isCodeValid ? 1 : 0, transition: 'opacity 0.4s ease', pointerEvents: 'none' }}>
              Very few ads may still remain on the players.
            </div>
          </div>
        </div>
      )}

      {/* Ad Overlay & Progress Bar */}
      {showAds && (
        <>
          <div className={`ad-progress-bar ${barVisible ? 'visible' : 'hidden'}`}>
            <div className="ad-progress-fill" style={{ width: `${barFillProgress * 100}%` }}></div>
            <span className="ad-progress-text" style={{ opacity: barTextOpacity }}>{barText}</span>
          </div>
          {overlayVisible && (
            <div className="ad-overlay" onClick={handleOverlayClick} style={{ pointerEvents: overlayClickable ? 'auto' : 'none' }} />
          )}
        </>
      )}

      {/* ===== STYLES (self-contained) ===== */}
      <style jsx>{`
        /* ----- AD OVERLAY ----- */
        .ad-overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100vw;
          height: 100vh;
          z-index: 9999;
          background: transparent;
          cursor: default;
        }

        /* ----- AD PROGRESS BAR ----- */
        .ad-progress-bar {
          position: fixed;
          top: 0;
          left: 50%;
          transform: translateX(-50%);
          width: 40%;
          height: 15px;
          background: rgba(20, 20, 30, 0.3);
          backdrop-filter: blur(5px) saturate(180%);
          -webkit-backdrop-filter: blur(5px) saturate(180%);
          border: 1px solid #ffffff34;
          border-radius: 0 0 8px 8px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
          overflow: hidden;
          z-index: 10000;
          opacity: 0;
          pointer-events: none;
          transition: opacity 0.5s ease, transform 0.5s ease;
          transform: translateX(-50%) translateY(-5px);
        }
        .ad-progress-bar.visible {
          opacity: 1;
          pointer-events: auto;
          transform: translateX(-50%) translateY(0);
        }
        .ad-progress-bar.hidden {
          opacity: 0;
          pointer-events: none;
          transform: translateX(-50%) translateY(-10px);
        }

        .ad-progress-fill {
          position: absolute;
          top: 0;
          left: 0;
          height: 100%;
          width: 0%;
          background: linear-gradient(70deg, #f7f5efad 0%, #f7f5ef88 80%, #ebe9e600 100%);
          transition: width 0.8s cubic-bezier(0.4, 0, 0.2, 1);
          z-index: 1;
        }

        .ad-progress-text {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: rgba(255, 255, 255, 0.7);
          font-size: 0.7rem;
          font-weight: 400;
          letter-spacing: 0.3px;
          text-shadow: 0 1px 2px rgba(0,0,0,0.4);
          z-index: 2;
          transition: opacity 0.3s ease;
        }

        @media (max-width: 480px) {
          .ad-progress-bar {
            width: 100%;
            height: 13px;
          }
          .ad-progress-text {
            font-size: 0.55rem;
          }
        }

        /* ----- USER ID POPUP (gift) ----- */
        .user-id-popup-overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100vw;
          height: 100vh;
          background: rgba(0, 0, 0, 0.28);
          backdrop-filter: blur(5px);
          -webkit-backdrop-filter: blur(5px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 99999;
          animation: fadeIn 0.3s ease-out;
        }

        .user-id-popup {
          position: relative;
          background: rgba(20, 20, 30, 0.95);
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
          border-radius: 32px;
          padding: 48px 56px 40px;
          text-align: center;
          max-width: 90%;
          width: 440px;
          box-shadow: 0 30px 80px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(255, 255, 255, 0.06);
          border: 1px solid rgba(255, 255, 255, 0.08);
        }

        .user-id-close-btn {
          position: absolute;
          top: 16px;
          right: 20px;
          background: transparent;
          border: none;
          color: rgba(255, 255, 255, 0.4);
          font-size: 1.4rem;
          cursor: pointer;
          transition: color 0.2s ease, transform 0.2s ease;
          padding: 4px 8px;
          line-height: 1;
        }

        .user-id-close-btn:hover {
          color: rgba(255, 255, 255, 0.9);
          transform: scale(1.15);
        }

        .user-id-title {
          font-family: Arial, Helvetica, sans-serif;
          font-size: 0.85rem;
          font-weight: 400;
          letter-spacing: 1.5px;
          text-transform: uppercase;
          color: rgba(255, 255, 255, 0.5);
          margin-bottom: 20px;
        }

        .gift-input {
          background: rgba(255, 255, 255, 0.04);
          border: 1px solid rgba(255, 255, 255, 0.15);
          border-radius: 16px;
          color: #fff;
          font-size: 1.8rem;
          font-weight: 600;
          padding: 14px 20px;
          width: 100%;
          text-align: center;
          letter-spacing: 3px;
          transition: border-color 0.3s ease;
        }
        .gift-input:focus {
          border-color: #59f001;
          outline: none;
        }
        .gift-input::placeholder {
          color: rgba(255, 255, 255, 0.3);
          letter-spacing: 1px;
          font-weight: 300;
        }

        .user-id-copy-btn {
          font-family: Arial, Helvetica, sans-serif;
          padding: 12px 36px;
          border-radius: 40px;
          font-size: 1rem;
          font-weight: 600;
          letter-spacing: 0.5px;
          border: none;
          transition: transform 0.25s ease, box-shadow 0.25s ease, background 0.3s ease;
          width: 100%;
          margin-top: 8px;
        }
        .user-id-copy-btn.valid:hover {
          transform: translateY(-2px) scale(1.02);
          box-shadow: 0 10px 32px rgba(212, 175, 55, 0.4);
        }
        .user-id-copy-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        @keyframes fadeIn {
          from { opacity: 0; transform: scale(0.96); }
          to { opacity: 1; transform: scale(1); }
        }

        @media (max-width: 480px) {
          .user-id-popup {
            padding: 30px 20px 28px;
            width: 88%;
          }
          .gift-input {
            font-size: 1.5rem;
            padding: 10px 14px;
          }
        }

        /* ----- Theme Discovery inside search input (glued) ----- */
        .theme-discovery-container {
          background: rgba(20, 20, 20, 0.98);
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.15);
          border-radius: 16px;
          padding: 20px 24px;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
          color: white;
          width: 100%;
          margin: 0;
        }

        .theme-discovery-title {
          font-size: 16px;
          font-weight: 600;
          margin-bottom: 6px;
          color: #59f001;
        }

        .theme-discovery-subtitle {
          font-size: 14px;
          color: rgba(255, 255, 255, 0.8);
          margin-bottom: 16px;
          line-height: 1.4;
        }

        .theme-suggestions-grid {
          display: grid;
          grid-template-columns: repeat(5, 1fr);
          gap: 10px;
          margin-bottom: 20px;
        }

        .theme-chip {
          background: rgba(255, 255, 255, 0.1);
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 30px;
          padding: 8px 0;
          text-align: center;
          font-size: 13px;
          color: white;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .theme-chip:hover {
          background: rgba(255, 255, 255, 0.2);
          border-color: rgba(255, 255, 255, 0.3);
        }

        .theme-got-it-btn {
          background: #59f001;
          color: #111;
          border: none;
          border-radius: 30px;
          padding: 8px 24px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          display: block;
          margin: 0 auto;
        }

        .theme-got-it-btn:hover {
          background: #e0b842;
          transform: scale(1.02);
          box-shadow: 0 5px 15px rgba(212, 175, 55, 0.4);
        }

        /* ----- Recommendation button & panel (glued) ----- */
        .recommend-btn-wrapper {
          width: 100%;
          display: flex;
          justify-content: center;
          margin-top: 10px;
        }

        .recommend-btn {
          padding: 10px 28px;
          background: rgba(255, 255, 255, 0.08);
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 40px;
          color: #fff;
          font-size: 15px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.3s ease;
          backdrop-filter: blur(4px);
          -webkit-backdrop-filter: blur(4px);
        }
        .recommend-btn:hover {
          background: rgba(255, 255, 255, 0.15);
          border-color: rgba(255, 255, 255, 0.4);
          transform: scale(1.02);
        }

        .recommendation-panel {
          width: 100%;
          max-width: 600px;
          margin: 10px auto 0;
          background: rgba(20, 20, 20, 0);
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
          border: 1px solid rgba(255, 255, 255, 0);
          border-radius: 16px;
          padding: 20px 24px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.6);
          color: white;
        }

        .recommendation-grid {
          display: grid;
          grid-template-columns: repeat(5, 1fr);
          gap: 10px;
          margin-bottom: 16px;
          transition: opacity 0.2s ease;
        }
        .recommendation-grid.fade {
          opacity: 0;
        }

        .recommendation-chip {
          background: rgba(255, 255, 255, 0.04);
          border: 1px solid rgba(255, 255, 255, 0.15);
          border-radius: 30px;
          padding: 8px 0;
          text-align: center;
          font-size: 13px;
          color: white;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        .recommendation-chip:hover {
          background: rgba(255, 255, 255, 0.18);
          border-color: rgba(255, 255, 255, 0.35);
          transform: scale(1.05);
        }

        .more-please-btn {
          background: rgba(255, 255, 255, 0.06);
          border: 1px solid rgba(255, 255, 255, 0.15);
          color: white;
          padding: 8px 20px;
          border-radius: 30px;
          font-size: 14px;
          cursor: pointer;
          transition: all 0.2s ease;
          display: block;
          margin: 0 auto;
        }
        .more-please-btn:hover {
          background: rgba(255, 255, 255, 0.12);
          border-color: rgba(255, 255, 255, 0.3);
        }

        /* ----- Search Input Container (fixed anchor) ----- */
        .search-input-container {
          position: fixed;
          width: 40%;
          top: 22%;
          left: 50%;
          transform: translate(-50%, -50%);
          z-index: 9999;
          display: flex;
          align-items: center;
          overflow: visible;
          /* No position:relative – fixed is enough for absolute children */
        }

        .search-input-container input {
          width: 100%;
          padding: 15px 25px;
          padding-right: 160px;
          font-size: 1.2rem;
          background: rgba(0, 0, 0, 0.527);
          backdrop-filter: blur(6px);
          -webkit-backdrop-filter: blur(8px);
          border: 1px solid rgba(255, 255, 255, 0.836);
          border-radius: 30px;
          color: white;
          outline: none;
          box-shadow: 0 8px 20px rgba(0, 0, 0, 0.4);
          transition: border-color 0.3s ease;
          height: 100%;
        }

        .search-input-container.keyword-mode input {
          border-color: #ffffff;
          box-shadow: 0 0 30px rgba(212, 175, 55, 0.2);
        }

        .toggle-switch-container {
          position: absolute;
          right: 0;
          top: 50%;
          transform: translateY(-50%);
          display: flex;
          align-items: center;
          background: rgba(255, 255, 255, 0.08);
          border-radius: 40px;
          padding: 4px;
          border: 1px solid rgba(255, 255, 255, 0.15);
          box-shadow: 0 2px 10px rgba(0,0,0,0.2);
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
          height: 96%;
          min-height: 34px;
          width: auto;
          min-width: 120px;
          margin-right: 1px;
        }

        .toggle-option {
          padding: 0 20px;
          font-size: 14px;
          font-weight: 600;
          color: rgba(255, 255, 255, 0.5);
          cursor: pointer;
          z-index: 2;
          transition: color 0.3s ease;
          white-space: nowrap;
          user-select: none;
          line-height: 1;
          display: flex;
          align-items: center;
          height: 100%;
        }

        .toggle-option.active {
          color: #111;
        }

        .toggle-slider {
          position: absolute;
          left: 4px;
          top: 4px;
          width: calc(50% - 4px);
          height: calc(100% - 8px);
          background: white;
          border-radius: 40px;
          transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
          z-index: 1;
          box-shadow: 0 2px 8px rgba(0,0,0,0.2);
        }

        .clear-search-btn {
          position: absolute;
          right: 170px;
          top: 50%;
          transform: translateY(-50%);
          background: transparent;
          border: none;
          color: rgba(255, 255, 255, 0.64);
          font-size: 22px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 8px;
          border-radius: 50%;
          transition: all 0.2s ease;
          z-index: 2;
        }

        .clear-search-btn:hover {
          color: white;
          background: rgba(255, 255, 255, 0);
        }

        /* Extra content glued below the search input */
        .search-extra {
          position: absolute;
          top: calc(100% + 20px);
          left: 0;
          width: 100%;
          z-index: 10;
          pointer-events: auto;
        }

        /* Responsive adjustments for search input & extra */
        @media (max-width: 1200px) {
          .search-input-container {
            width: 80%;
            top: 28%;
          }
        }

        @media (max-width: 768px) {
          .search-input-container {
            width: 90%;
            top: 19vh;
          }
          .search-input-container input {
            padding-right: 120px;
            font-size: 1rem;
          }
          .toggle-switch-container {
            min-width: 100px;
            margin-right: 1px;
          }
          .toggle-option {
            font-size: 12px;
            padding: 0 14px;
          }
          .clear-search-btn {
            right: 130px;
          }
          .theme-suggestions-grid {
            grid-template-columns: repeat(2, 1fr);
          }
          .recommendation-grid {
            grid-template-columns: repeat(2, 1fr);
          }
          .remove-history-btn {
            opacity: 0.8 !important;
            width: 35px;
            height: 35px;
            top: -2px;
            right: -3px;
          }
          .history-item-wrapper:hover .movie-card {
            transform: none;
            box-shadow: none;
          }
          .history-item-wrapper .remove-history-btn:hover ~ .movie-card,
          .history-item-wrapper:hover .movie-card {
            transform: none;
            box-shadow: none;
          }
        }

        /* ----- Search results with extra content spacing ----- */
        .search-results {
          flex-grow: 1;
          overflow-y: auto;
          padding: 20px;
          margin-top: 120px;
        }

        .search-results.has-extra .results-grid {
          padding-top: 280px;
        }
        .search-results.has-extra .no-results,
        .search-results.has-extra .loading-indicator {
          margin-top: 280px;
        }

        .results-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
          gap: 20px;
          padding-top: 150px;
        }

        @media (min-width: 860px) {
          .results-grid {
            grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
          }
        }

        @media (min-width: 400px) and (max-width: 500px) {
          .results-grid {
            padding-top: 210px;
          }
          .search-input-container {
            top: 230px !important;
          }
        }
           @media (min-width: 300px) and (max-width: 400px) {
          .results-grid {
            padding-top: 180px;
          }
          .search-input-container {
            top: 200px !important;
          }
        }


        /* ----- Active indicator sliding transition (restored) ----- */
        .active-indicator {
          position: absolute;
          bottom: 0;
          height: 3px;
          background: linear-gradient(to right, #fdfdfd, #dbdbda);
          border-radius: 2px 2px 0 0;
          transition: left 0.45s cubic-bezier(0.34, 1.56, 0.64, 1),
                      width 0.45s cubic-bezier(0.34, 1.56, 0.64, 1);
          z-index: 2;
          box-shadow: 0 0 10px rgba(212, 175, 55, 0.3);
          opacity: 1;
        }
        .active-indicator.fade-out {
          opacity: 0;
          transition: opacity 0.15s ease;
        }

        /* ----- Static line ----- */
        .static-line-container {
          position: relative;
          bottom: 0;
          left: 0;
          width: 100%;
          height: 1px;
          display: flex;
          justify-content: center;
          pointer-events: none;
          z-index: 1;
        }

        .static-line {
          height: 1px;
          width: 95%;
          background: linear-gradient(to right, transparent, rgba(255,255,255,0.3), transparent);
        }

        /* ----- History item wrapper (remove button) ----- */
        .history-item-wrapper {
          position: relative;
          transition: transform 0.3s ease, box-shadow 0.3s ease;
          display: inline-block;
        }
        .history-item-wrapper:hover {
          z-index: 2;
        }
        .history-item-wrapper:hover .movie-card {
          box-shadow: 0 5px 15px rgba(0, 0, 0, 0.5);
        }
        .remove-history-btn {
          position: absolute;
          top: -2px;
          right: 2px;
          width: 24px;
          height: 24px;
          border-radius: 50%;
          color: white;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 0;
          transition: opacity 0.3s ease;
          z-index: 10;
        }
        .history-item-wrapper:hover .remove-history-btn {
          opacity: 1;
        }
        .history-item-wrapper .remove-history-btn:hover ~ .movie-card,
        .history-item-wrapper:hover .movie-card {
          transform: scale(1.05);
          box-shadow: 0 5px 15px rgba(0, 0, 0, 0.5);
        }

        @media (max-width: 768px) {
          .remove-history-btn {
            opacity: 0.8 !important;
            width: 35px;
            height: 35px;
            top: -2px;
            right: -3px;
          }
          .history-item-wrapper:hover .movie-card {
            transform: none;
            box-shadow: none;
          }
          .history-item-wrapper .remove-history-btn:hover ~ .movie-card,
          .history-item-wrapper:hover .movie-card {
            transform: none;
            box-shadow: none;
          }
        }

        /* ----- Forbidden word warning ----- */
        .forbidden-word-warning {
          position: fixed;
          top: calc(18vh + 140px + 20px);
          left: 50%;
          transform: translateX(-50%);
          background: rgba(255, 0, 0, 0.8);
          color: white;
          padding: 8px 20px;
          border-radius: 8px;
          font-size: 14px;
          z-index: 99999;
          animation: fadeInUp 0.3s ease;
        }

        /* ----- Load more, no more results ----- */
        .load-more-container {
          display: flex;
          justify-content: center;
          margin: 25px 0;
          width: 100%;
        }
        .load-more-btn {
          color: white;
          border: 1px solid rgba(255,255,255,0.3);
          border-radius: 30px;
          padding: 12px 35px;
          font-size: 1.05rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          position: relative;
          overflow: hidden;
          background: transparent;
        }
        .load-more-btn:hover {
          transform: translateY(-3px);
          box-shadow: 0 6px 20px rgba(255, 255, 255, 0.1);
          background: rgba(255,255,255,0.05);
        }
        .load-more-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }

        .no-more-results {
          text-align: center;
          padding: 20px;
          color: rgba(255,255,255,0.5);
          font-size: 14px;
        }

        /* ----- Loading indicator ----- */
        .loading-indicator {
          display: flex;
          justify-content: center;
          align-items: center;
          height: 100px;
          color: rgba(255,255,255,0.7);
          font-size: 16px;
        }
        .loading-indicator .fa-spinner {
          margin-right: 8px;
        }

        /* ----- No results ----- */
        .no-results {
          text-align: center;
          padding: 40px;
          color: rgba(255,255,255,0.6);
          font-size: 16px;
        }

        /* ----- History title ----- */
        .history-title {
          font-size: 24px;
          font-weight: 700;
          color: white;
          text-align: center;
          padding: 20px 0;
        }
      `}</style>
    </>
  );
};

export default Navbar;